import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Page, Pageable } from '../../../model/rest/base';
import {
  Group,
  GroupDetails,
  GroupMember,
  GroupMemberDetails,
  GroupStudentRequest,
  GroupTeacherRequest,
  GroupRef,
  MemberRole,
  SchoolMemberRef,
  V3GroupStudent,
  V3GroupTeacher,
} from '../../../model/v3/schools';
import { appendIfDefined, createPageableParams } from './rest-v3-utils';

@Injectable({
  providedIn: 'root',
})
export class GroupV3RestService {
  constructor(private http: HttpClient) {}

  private url(schoolId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/groups${path}`;
  }

  listSchoolGroups(schoolId: number, pageable: Pageable): Observable<Page<Group>> {
    const params = createPageableParams(pageable);
    return this.http.get<Page<Group>>(this.url(schoolId), { params });
  }

  getGroup(schoolId: number, groupId: number): Observable<Group | null> {
    return this.http.get<Group | null>(this.url(schoolId, `/${groupId}`));
  }

  createNewGroup(schoolId: number, details: GroupDetails): Observable<GroupRef> {
    return this.http.post<GroupRef>(this.url(schoolId), details);
  }

  updateGroup(schoolId: number, groupId: number, details: GroupDetails): Observable<void> {
    return this.http.put<void>(this.url(schoolId, `/${groupId}`), details);
  }

  getGroupMembers(
    schoolId: number,
    groupId: number,
    pageable: Pageable,
    role?: MemberRole | null,
  ): Observable<Page<GroupMember<SchoolMemberRef>>> {
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, 'role', role);
    return this.http.get<Page<GroupMember<SchoolMemberRef>>>(
      this.url(schoolId, `/${groupId}/members`),
      { params },
    );
  }

  addGroupStudent(
    schoolId: number,
    groupId: number,
    details: GroupStudentRequest,
  ): Observable<V3GroupStudent> {
    const params = new HttpParams().set('role', MemberRole.Student);
    return this.http.post<V3GroupStudent>(
      this.url(schoolId, `/${groupId}/members`),
      details,
      { params },
    );
  }

  addGroupTeacher(
    schoolId: number,
    groupId: number,
    details: GroupTeacherRequest,
  ): Observable<V3GroupTeacher> {
    const params = new HttpParams().set('role', MemberRole.Teacher);
    return this.http.post<V3GroupTeacher>(
      this.url(schoolId, `/${groupId}/members`),
      details,
      { params },
    );
  }

  updateMemberDetails(
    schoolId: number,
    groupId: number,
    memberId: number,
    details: GroupMemberDetails,
  ): Observable<void> {
    return this.http.put<void>(
      this.url(schoolId, `/${groupId}/members/${memberId}`),
      details,
    );
  }

  deleteMember(
    schoolId: number,
    groupId: number,
    memberId: number,
  ): Observable<boolean> {
    return this.http.delete<boolean>(this.url(schoolId, `/${groupId}/members/${memberId}`));
  }

  deleteGroup(schoolId: number, groupId: number): Observable<boolean> {
    return this.http.delete<boolean>(this.url(schoolId, `/${groupId}`));
  }
}
